import React, { useState, memo } from 'react';
import { Check, ArrowRight, ShieldCheck, Download, Infinity, Mail, Printer, Flame } from 'lucide-react';
import { BonusCard } from './components/BonusCard';
import { PurchaseNotification } from './components/PurchaseNotification';
import { InlineTimer } from './components/InlineTimer';
import { TestimonialCard } from './components/TestimonialCard';
const CHECKOUT_URL = 'https://psicochek.librosdigitales.pro/cart/42840633147455:1';
const MOCKUP_IMAGE = 'https://cdn.magicpatterns.com/uploads/gP6QuGfmJNLScn8bussM7X/nuevo_mock_up_psicopedagogia-removebg-preview.png';
interface PlanOption {
  id: string;
  emoji: string;
  title: string;
  description: string;
  oldPrice: string;
  price: string;
  checkoutUrl: string;
  highlighted?: boolean;
  badge?: string;
  includes: string[];
}
const PLAN_OPTIONS: PlanOption[] = [{
  id: 'base',
  emoji: '📦',
  title: 'MEGAPACK BASE',
  description: 'Incluye TDAH + Dislexia · Material completo + 5 bonos profesionales',
  oldPrice: '$79.000',
  price: '$17.990',
  checkoutUrl: 'https://psicochek.librosdigitales.pro/cart/42840633147455:1',
  includes: ['+100 páginas de material clínico', 'Sistemas para TDAH, Dislexia y TEA', '5 bonos profesionales incluidos', 'Acceso inmediato y de por vida']
}, {
  id: 'antidispersion',
  emoji: '🧠',
  title: 'MEGAPACK + ANTIDISPERSIÓN + CRISIS',
  description: 'Incluye TDAH + Dislexia · Todo el MegaPack + Antidispersión + Crisis en Sesión',
  oldPrice: '$83.990',
  price: '$19.990',
  checkoutUrl: 'https://psicochek.librosdigitales.pro/cart/42924756140095:1',
  includes: ['Todo lo del MegaPack Base', '20 fichas Antidispersión: técnicas de recuperación del foco cuando el niño se dispersa en sesión', '20 fichas Crisis en Sesión: protocolos paso a paso para cuando el niño entra en llanto, enojo o bloqueo', '40 fichas extra para sostener cualquier situación clínica']
}, {
  id: 'adolescentes',
  emoji: '🎓',
  title: 'MEGAPACK + ADOLESCENTES',
  description: 'Incluye TDAH + Dislexia · Todo el MegaPack + 50 actividades cognitivas para adolescentes de 12 a 18 años',
  oldPrice: '$83.990',
  price: '$23.990',
  checkoutUrl: 'https://psicochek.librosdigitales.pro/cart/42986957045823:1',
  includes: ['Todo lo del MegaPack Base', '50 actividades cognitivas para adolescentes (12-18 años)', 'Material con estética adaptada a la edad', 'Ejercicios de atención, memoria y funciones ejecutivas']
}, {
  id: 'completo',
  emoji: '⚡',
  title: 'MEGAPACK COMPLETO',
  description: 'Todo incluido: MegaPack Base + Antidispersión + Crisis en Sesión + Adolescentes',
  oldPrice: '$88.990',
  price: '$27.990',
  checkoutUrl: 'https://psicochek.librosdigitales.pro/cart/42986957144127:1',
  highlighted: true,
  badge: 'MÁS ELEGIDO',
  includes: ['MegaPack Base (+100 páginas, TDAH, Dislexia, TEA)', '20 fichas Antidispersión (recuperación del foco)', '20 fichas Crisis en Sesión (llanto, enojo, bloqueo)', '50 actividades cognitivas para adolescentes (12-18 años)']
}];
export function App() {
  const [selectedPlan, setSelectedPlan] = useState('completo');
  const currentPlan = PLAN_OPTIONS.find(p => p.id === selectedPlan)!;
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-0">
      <PurchaseNotification data-id="element-1" />

      {/* 1. HERO SECTION - Fits exactly on iPhone screen */}
      <header className="bg-gradient-to-b from-pastel-50 to-white min-h-[100svh] flex flex-col justify-center px-4 py-4 md:min-h-0 md:pt-16 md:pb-16 overflow-hidden" data-id="element-2">
        <div className="max-w-5xl mx-auto text-center" data-id="element-3">
          <div className="inline-block bg-pastel-100 text-pastel-800 px-3 py-1 md:px-4 md:py-1.5 rounded-full text-[9px] md:text-sm font-bold tracking-wide mb-2 md:mb-6 leading-tight" data-id="element-4">
            PARA PSICOPEDAGOGAS QUE QUIEREN RECUPERAR SUS TARDES LIBRES
          </div>

          <h1 className="text-[22px] md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-[1.15] mb-2 md:mb-6 max-w-4xl mx-auto" data-id="element-5">
            DEJÁ DE TERMINAR EL DÍA AGOTADA PLANIFICANDO LA SESIÓN DE MAÑANA.
          </h1>

          <p className="text-[12px] md:text-xl text-gray-600 font-medium mb-3 md:mb-4 max-w-3xl mx-auto leading-snug" data-id="element-6">
            El primer sistema clínico que te da 1 año de material listo para
            usar y las herramientas para demostrarle a los padres —con datos
            reales— el avance de sus hijos.
          </p>

          <div className="relative max-w-[240px] md:max-w-2xl mx-auto mb-3 md:mb-8" data-id="element-7">
            <a href="#compra" className="block group" data-id="element-8">
              <img src={MOCKUP_IMAGE} alt="MegaPack Psicopedagógico Profesional" className="w-full h-auto drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" data-id="element-9" />
            </a>
          </div>

          <div className="flex flex-col items-center gap-0.5 md:gap-2 mb-2 md:mb-6 text-[11px] md:text-base font-medium text-gray-700" data-id="element-10">
            <span className="flex items-center gap-1 md:gap-2" data-id="element-11">
              <Check className="w-3.5 h-3.5 md:w-5 md:h-5 text-emerald-500" data-id="element-12" />
              24 sesiones estructuradas: Chau a la improvisación.
            </span>
            <span className="flex items-center gap-1 md:gap-2" data-id="element-13">
              <Check className="w-3.5 h-3.5 md:w-5 md:h-5 text-emerald-500" data-id="element-14" />
              Sistemas para TDAH, Dislexia y TEA.
            </span>
            <span className="flex items-center gap-1 md:gap-2" data-id="element-15">
              <Check className="w-3.5 h-3.5 md:w-5 md:h-5 text-emerald-500" data-id="element-16" />
              Kit de Comunicación con Padres.
            </span>
          </div>

          <p className="text-[12px] md:text-xl text-gray-600 font-medium mb-3 md:mb-8" data-id="element-17">
            Descargás. Imprimís. Recuperás tu tiempo.
          </p>

          <div className="max-w-md mx-auto" data-id="element-18">
            <a href="#compra" className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm md:text-xl font-bold py-3 md:py-4 px-5 md:px-8 rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 md:gap-3 w-full" data-id="element-19">
              ACCEDER AL MEGAPACK AHORA
              <ArrowRight className="w-4 h-4 md:w-6 md:h-6" data-id="element-20" />
            </a>
            <p className="text-[11px] md:text-sm text-gray-500 mt-2 md:mt-4 font-medium" data-id="element-21">
              Pago único de $17.990 ARS · Acceso inmediato
            </p>
          </div>
        </div>
      </header>

      {/* 2. CONEXIÓN EMOCIONAL */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-22">
        <div className="max-w-3xl mx-auto" data-id="element-23">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-10 md:mb-12 text-center" data-id="element-24">
            SI SOS PSICOPEDAGOGA, ESTO TE VA A SONAR FAMILIAR...
          </h2>

          <div className="space-y-5 md:space-y-6 text-base md:text-2xl text-gray-700 leading-relaxed text-center font-medium max-w-2xl mx-auto" data-id="element-25">
            <p data-id="element-26">
              Terminás tu última consulta del día. Estás cansada, pero el
              trabajo real recién empieza.
            </p>
            <p data-id="element-27">
              Abrís la computadora. Buscás en Pinterest. Saltás a grupos de
              Facebook. Terminás en Canva tratando de "tunear" una ficha
              infantil para que parezca profesional.
            </p>
            <p className="text-pastel-700 font-bold" data-id="element-28">
              Son las 9 de la noche y todavía no sabés qué vas a hacer mañana a
              las 8 AM.
            </p>
            <p data-id="element-29">
              La cabeza no se apaga. Y no es por el compromiso con los niños...
              es porque sostenés todo el sistema sola.
            </p>
            <p data-id="element-30">
              Peor aún: llega la reunión con los padres y sentís que te faltan
              palabras para explicarles por qué el niño está avanzando. Querés
              mostrarles resultados, pero no tenés cómo tangibilizar ese
              progreso.
            </p>
            <p className="text-rose-700 font-bold text-lg md:text-3xl pt-4" data-id="element-31">
              Cada semana empezando de cero. Cada reunión de padres bajo
              presión.
            </p>
          </div>
        </div>
      </section>

      {/* 3. LA SOLUCIÓN */}
      <section className="py-16 md:py-20 px-4 bg-pastel-100 text-center" data-id="element-32">
        <div className="max-w-4xl mx-auto" data-id="element-33">
          <span className="text-amber-600 font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-34">
            UN SISTEMA QUE TE SOSTIENE (Y TE DA AUTORIDAD)
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-8 text-gray-900" data-id="element-35">
            MegaPack Psicopedagógico Profesional™
          </h2>

          <p className="text-lg md:text-2xl text-gray-700 font-semibold mb-10 max-w-3xl mx-auto" data-id="element-36">
            Hemos creado un sistema completo para que dejes de "inventar"
            actividades y empieces a liderar procesos clínicos.
          </p>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-3xl mx-auto text-left" data-id="element-37">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-pastel-200" data-id="element-38">
              <span className="text-2xl mb-3 block" data-id="element-39">⏰</span>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3" data-id="element-40">
                RECUPERÁS TU TIEMPO
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-41">
                Entrás al consultorio con claridad absoluta. El material ya está
                validado y ordenado por sesiones. Solo abrís, imprimís y
                aplicás.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-pastel-200" data-id="element-42">
              <span className="text-2xl mb-3 block" data-id="element-43">👨‍👩‍👧</span>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3" data-id="element-44">
                FIDELIZÁS A LOS PADRES
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-45">
                Usás nuestras planillas de seguimiento para que la familia note
                el avance sesión a sesión. Ya no "contás" lo que hiciste; lo
                mostrás profesionalmente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. QUÉ INCLUYE */}
      <section className="py-16 md:py-20 px-4 bg-gray-50" data-id="element-46">
        <div className="max-w-4xl mx-auto" data-id="element-47">
          <div className="text-center mb-10 md:mb-12" data-id="element-48">
            <span className="text-4xl mb-4 block" data-id="element-49">📦</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3" data-id="element-50">
              QUÉ INCLUYE EL MEGAPACK
            </h2>
            <p className="text-gray-600 text-base md:text-lg" data-id="element-51">
              Lo que necesitás para que el consultorio funcione en piloto
              automático:
            </p>
          </div>

          <div className="space-y-4 md:space-y-5 mb-10" data-id="element-52">
            {[{
            icon: '📄',
            title: '+100 páginas de material clínico',
            desc: 'No son "dibujitos", es material diseñado para intervención real.'
          }, {
            icon: '🧠',
            title: 'Sistemas TDAH, Dislexia y TEA',
            desc: 'TDAH: 24 sesiones estructuradas + 120 fichas para atención e inhibición. Dislexia: 72 actividades progresivas de lectura y escritura. TEA: Ejercicios para atención conjunta, regulación emocional y comunicación funcional.'
          }, {
            icon: '📊',
            title: 'Planillas de Seguimiento Clínico',
            desc: 'Tu respaldo para saber exactamente dónde está parado cada paciente.'
          }, {
            icon: '👨‍👩‍👧',
            title: 'Reportes de Valor para Padres',
            desc: 'La herramienta estratégica para comunicar avances y que los padres valoren cada centavo de tu honorario.'
          }].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-5 md:p-6 shadow-sm border border-gray-100" data-id="element-53">
                <span className="text-2xl flex-shrink-0 mt-0.5" data-id="element-54">
                  {item.icon}
                </span>
                <div data-id="element-55">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1" data-id="element-56">
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600" data-id="element-57">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>

          <div className="flex justify-center" data-id="element-58">
            <a href="#compra" className="block group" data-id="element-59">
              <img src={MOCKUP_IMAGE} alt="Qué incluye el MegaPack" className="w-[280px] md:w-[400px] drop-shadow-xl group-hover:scale-105 transition-transform duration-500" data-id="element-60" />
            </a>
          </div>
        </div>
      </section>

      {/* 5. BONOS PROFESIONALES */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-61">
        <div className="max-w-7xl mx-auto" data-id="element-62">
          <div className="text-center mb-12 md:mb-16" data-id="element-63">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3" data-id="element-64">
              <span data-id="element-65">🎁</span> BONOS PROFESIONALES
            </h2>
            <p className="text-lg md:text-xl text-gray-600 font-medium" data-id="element-66">
              GRATIS HOY con tu MegaPack:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-67">
            <BonusCard number={1} title="Guía de Estrategias Terapéuticas" description="Intervenciones prácticas para que sepas qué hacer cuando la sesión se pone difícil." image="https://cdn.magicpatterns.com/uploads/gMdT3WBzziTMuJSZFjDdfe/c3a2b0e9-7269-42e6-8eb5-fad89736cdde-removebg-preview.png" data-id="element-68" />
            <BonusCard number={2} title="Kit de Reportes para Padres (EL PREFERIDO)" description="Dejá de perder horas redactando informes. Usá estas plantillas profesionales para comunicar progresos de forma clara y técnica. Hacé que tu trabajo hable por vos." image="https://cdn.magicpatterns.com/uploads/6483v4TDdPnNCaaLS7obqY/64501d31-5600-4e64-9b1f-6bef7b5f0096-removebg-preview.png" data-id="element-69" />
            <BonusCard number={3} title="Checklist de Sesión Exitosa" description="La lista para organizar tu consultorio en 60 segundos y no olvidar ningún detalle clínico." image="https://cdn.magicpatterns.com/uploads/2Uv3hUNxq8z8J5ptJtbZRi/44941fd4-0a91-47c6-a4fc-0907a92fb06d-removebg-preview.png" data-id="element-70" />
            <BonusCard number={4} title="Cuaderno de Regulación Emocional" description="Herramientas listas para trabajar ansiedad y autocontrol, los motivos de consulta más frecuentes." image="https://cdn.magicpatterns.com/uploads/6emCNR8cauzW1F9u81jn29/0439c90e-8404-435e-aeb3-dbb53b7bc719-removebg-preview.png" data-id="element-71" />
          </div>
        </div>
      </section>

      {/* 6. ESTE MEGAPACK ES PARA VOS SI */}
      <section className="py-16 md:py-20 px-4 bg-pastel-50" data-id="element-72">
        <div className="max-w-3xl mx-auto" data-id="element-73">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10 md:mb-12" data-id="element-74">
            ESTE MEGAPACK ES PARA VOS SI
          </h2>

          <div className="space-y-4 md:space-y-5 mb-16" data-id="element-75">
            {['Atendés niños y sentís que la preparación de materiales te está "comiendo" la vida personal.', 'Querés dejar de trabajar gratis los domingos y feriados planificando.', 'Necesitás una forma profesional de demostrarle a los padres que el niño está progresando gracias a tu intervención.'].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm border border-pastel-200" data-id="element-76">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-77">
                  <Check className="w-5 h-5 text-emerald-600" data-id="element-78" />
                </div>
                <span className="text-base md:text-lg text-gray-800 font-medium leading-relaxed" data-id="element-79">
                  {item}
                </span>
              </div>)}
          </div>

          {/* AUTORA */}
          <div className="bg-white rounded-2xl p-6 md:p-10 shadow-md border border-pastel-200 flex flex-col md:flex-row items-center gap-8" data-id="element-80">
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden flex-shrink-0 border-4 border-pastel-200 shadow-lg" data-id="element-81">
              <img src="https://cdn.magicpatterns.com/uploads/76Ts6t7DHWJboFyXEsAcGo/WhatsApp_Image_2026-03-06_at_4.35.36_PM.jpg" alt="Lic. Mariana Estrecher" className="w-full h-full object-cover" data-id="element-82" />
            </div>
            <div className="text-center md:text-left" data-id="element-83">
              <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-84">
                Lic. Mariana Estrecher
              </h3>
              <p className="text-pastel-700 font-semibold mb-4" data-id="element-85">
                Psicopedagoga
              </p>
              <p className="text-gray-600 leading-relaxed mb-3" data-id="element-86">
                Especialista en intervención en dificultades de aprendizaje.
              </p>
              <p className="text-gray-600 leading-relaxed mb-3" data-id="element-87">
                Junto a otras colegas psicopedagogas desarrolló este sistema de
                materiales profesionales basados en la experiencia real de
                trabajo en consultorio con niños que presentan TDAH, dislexia y
                dificultades en la lectoescritura.
              </p>
              <p className="text-gray-700 leading-relaxed font-medium" data-id="element-88">
                Hoy estas herramientas ayudan a profesionales a organizar mejor
                sus sesiones, trabajar con más seguridad clínica y acompañar el
                progreso de cada niño de forma estructurada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. ACCESO INMEDIATO */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-89">
        <div className="max-w-3xl mx-auto" data-id="element-90">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4" data-id="element-91">
            ACCESO INMEDIATO
          </h2>
          <p className="text-center text-gray-600 text-lg mb-10" data-id="element-92">
            Después de comprar recibís:
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-93">
            {[{
            icon: <Download className="w-7 h-7 text-teal-600" data-id="element-94" />,
            text: 'Acceso inmediato al material',
            bg: 'bg-teal-50',
            border: 'border-teal-100'
          }, {
            icon: <Mail className="w-7 h-7 text-pastel-600" data-id="element-95" />,
            text: 'Descarga digital en PDF',
            bg: 'bg-pastel-50',
            border: 'border-pastel-200'
          }, {
            icon: <Infinity className="w-7 h-7 text-amber-600" data-id="element-96" />,
            text: 'Acceso de por vida',
            bg: 'bg-amber-50',
            border: 'border-amber-100'
          }, {
            icon: <Printer className="w-7 h-7 text-rose-600" data-id="element-97" />,
            text: 'Podés imprimir cuando lo necesites',
            bg: 'bg-rose-50',
            border: 'border-rose-100'
          }].map((item, i) => <div key={i} className={`${item.bg} rounded-xl p-5 text-center border ${item.border}`} data-id="element-98">
                <div className="flex justify-center mb-3" data-id="element-99">{item.icon}</div>
                <p className="text-sm md:text-base text-gray-700 font-medium" data-id="element-100">
                  {item.text}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. RESULTADO */}
      <section className="py-16 md:py-20 px-4 bg-gray-50" data-id="element-101">
        <div className="max-w-3xl mx-auto text-center" data-id="element-102">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-10" data-id="element-103">
            EL RESULTADO
          </h2>

          <div className="space-y-5 md:space-y-6 text-base md:text-2xl text-gray-700 leading-relaxed font-medium max-w-2xl mx-auto" data-id="element-104">
            <p data-id="element-105">Entrás al consultorio sabiendo exactamente qué trabajar.</p>
            <p data-id="element-106">Las sesiones fluyen porque hay un orden.</p>
            <p data-id="element-107">Los padres están tranquilos porque ven reportes claros.</p>
            <p className="pt-4 text-lg md:text-2xl" data-id="element-108">
              Y lo más importante de todo:
            </p>
            <p className="text-pastel-700 font-bold text-xl md:text-3xl" data-id="element-109">
              Terminás tu jornada y la jornada se termina de verdad.
            </p>
            <p className="text-teal-700 font-bold text-lg md:text-2xl" data-id="element-110">
              Volvés a ser dueña de tu tiempo.
            </p>
          </div>
        </div>
      </section>

      {/* 9. TESTIMONIOS */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-111">
        <div className="max-w-4xl mx-auto" data-id="element-112">
          <div className="mb-10 md:mb-12 flex justify-center" data-id="element-113">
            <img src="https://cdn.magicpatterns.com/uploads/ayzify2pMatZdb8zwZHSi5/psicopedagoga.jpg" alt="Información importante y cuenta oficial de contacto" className="w-full max-w-2xl rounded-2xl shadow-lg" data-id="element-114" />
          </div>

          <div className="text-center mb-12" data-id="element-115">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 flex items-center justify-center gap-3" data-id="element-116">
              <span data-id="element-117">💬</span> LO QUE DICEN LAS COLEGAS
            </h2>
            <p className="text-gray-600 mt-3 text-lg" data-id="element-118">
              Psicopedagogas que ya usan el MegaPack en su consultorio
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-119">
            <TestimonialCard name="Valentina R." role="Psicopedagoga clínica" location="Buenos Aires" content="Antes perdía horas buscando actividades. Ahora abro el MegaPack y tengo todo listo. Me cambió la forma de trabajar." data-id="element-120" />
            <TestimonialCard name="Camila S." role="Psicopedagoga infantil" location="Córdoba" content="El sistema de TDAH es increíble. Las 24 sesiones están tan bien pensadas que mis pacientes avanzan mucho más rápido." data-id="element-121" />
            <TestimonialCard name="Lucía M." role="Psicopedagoga" location="Rosario" content="Lo mejor es el material de dislexia. Las actividades progresivas me permiten hacer un seguimiento real del proceso de cada niño." data-id="element-122" />
            <TestimonialCard name="Fernanda G." role="Psicopedagoga clínica" location="Mendoza" content="Los reportes para padres me ahorraron horas de trabajo. Ahora puedo comunicar avances de forma profesional y clara." data-id="element-123" />
          </div>
        </div>
      </section>

      {/* 10. PRICING SELECTOR SECTION */}
      <section id="compra" className="py-3 px-2.5 bg-pastel-100 relative overflow-hidden scroll-mt-0" data-id="element-124">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-125">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-pastel-300 rounded-full blur-3xl" data-id="element-126"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-pastel-200 rounded-full blur-3xl" data-id="element-127"></div>
        </div>

        <div className="max-w-2xl mx-auto relative z-10" data-id="element-128">
          <InlineTimer data-id="element-129" />

          <div className="text-center mt-2 mb-2 md:mt-4 md:mb-4" data-id="element-130">
            <h2 className="text-xl md:text-5xl font-extrabold text-gray-900" data-id="element-131">
              Elegí tu <span className="text-pastel-700" data-id="element-132">MEGAPACK:</span>
            </h2>
            <p className="text-gray-600 mt-0.5 text-[10px] md:text-lg" data-id="element-133">
              Todas las opciones incluyen TDAH + Dislexia + TEA y los 5 bonos
            </p>
          </div>

          {/* What's included in selected plan */}
          <div className="mb-3 md:mb-4 bg-white rounded-xl p-3 md:p-8 border border-pastel-200 shadow-sm" data-id="element-134">
            <p className="text-[10px] md:text-xs font-bold text-pastel-700 uppercase tracking-wider mb-2 md:mb-3" data-id="element-135">
              {currentPlan.title} INCLUYE:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 md:gap-2" data-id="element-136">
              {currentPlan.includes.map((item, i) => <div key={i} className="flex items-start gap-1" data-id="element-137">
                  <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-138" />
                  <span className="text-[10px] md:text-base text-gray-700" data-id="element-139">
                    {item}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Plan Options */}
          <div className="space-y-1.5 mb-3 md:space-y-2 md:mb-4" data-id="element-140">
            {PLAN_OPTIONS.map(plan => {
            const isSelected = selectedPlan === plan.id;
            return <button key={plan.id} onClick={() => setSelectedPlan(plan.id)} className={`w-full text-left rounded-lg p-2 md:rounded-xl md:p-6 transition-all duration-200 border-2 ${isSelected ? 'border-pastel-500 bg-white shadow-lg' : 'border-pastel-200 bg-white/60 hover:border-pastel-300 hover:bg-white/80'}`} data-id="element-141">
                  <div className="flex items-center gap-2 md:gap-3" data-id="element-142">
                    <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-pastel-600' : 'border-pastel-300'}`} data-id="element-143">
                      {isSelected && <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-pastel-600" data-id="element-144"></div>}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-145">
                      <div className="flex items-center gap-1 md:gap-1.5 flex-wrap" data-id="element-146">
                        <span className="text-xs md:text-sm" data-id="element-147">{plan.emoji}</span>
                        <h3 className="text-[11px] md:text-xl font-extrabold text-gray-900 leading-tight" data-id="element-148">
                          {plan.title}
                        </h3>
                        {plan.badge && <span className="bg-amber-400 text-amber-900 text-[8px] md:text-xs font-bold px-1 py-0.5 rounded-full" data-id="element-149">
                            ⭐ {plan.badge}
                          </span>}
                      </div>
                      {plan.id === 'antidispersion' && <p className="text-[9px] md:text-sm text-gray-500 mt-0 leading-tight" data-id="element-150">
                          + 20 fichas recuperar foco + 20 fichas crisis
                          emocionales
                        </p>}
                      {plan.id === 'adolescentes' && <p className="text-[9px] md:text-sm text-gray-500 mt-0 leading-tight" data-id="element-151">
                          + 50 actividades cognitivas para adolescentes (12-18
                          años)
                        </p>}
                      {plan.id === 'completo' && <p className="text-[9px] md:text-sm text-gray-500 mt-0 leading-tight" data-id="element-152">
                          Base + Antidispersión + Crisis + Adolescentes · Todo
                          incluido
                        </p>}
                    </div>

                    <div className="text-right flex-shrink-0" data-id="element-153">
                      <p className="text-[9px] md:text-sm text-gray-400 line-through" data-id="element-154">
                        {plan.oldPrice}
                      </p>
                      <p className={`text-base md:text-3xl font-extrabold ${isSelected ? 'text-pastel-700' : 'text-gray-900'}`} data-id="element-155">
                        {plan.price}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* CTA Button */}
          <p className="text-center text-[10px] md:text-sm text-gray-500 mb-2 md:mb-3" data-id="element-156">
            Al hacer clic vas a ser redirigido a Mercado Pago para completar el
            pago de forma segura. Tu acceso se habilita automáticamente después
            de pagar.
          </p>
          <a href={currentPlan.checkoutUrl} className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm md:text-2xl font-bold py-3 px-5 md:py-5 md:px-8 rounded-xl shadow-[0_0_40px_rgba(16,185,129,0.4)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 w-full" data-id="element-157">
            SÍ, QUIERO {currentPlan.title} →
          </a>

          <p className="text-center text-gray-500 text-[10px] md:text-xs mt-1.5 italic" data-id="element-158">
            Podés cambiar tu opción en cualquier momento
          </p>
        </div>
      </section>

      {/* 11. GARANTÍA */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-159">
        <div className="max-w-3xl mx-auto" data-id="element-160">
          <div className="bg-emerald-50 rounded-3xl p-8 md:p-12 border-2 border-emerald-100 text-center md:text-left flex flex-col md:flex-row items-center gap-8 shadow-lg" data-id="element-161">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-md flex-shrink-0" data-id="element-162">
              <ShieldCheck className="w-12 h-12 text-emerald-500" data-id="element-163" />
            </div>
            <div data-id="element-164">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-165">
                GARANTÍA DE PAZ MENTAL (7 DÍAS)
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-2" data-id="element-166">
                Probá el material. Si después de una semana no sentís que
                recuperaste horas de sueño y que tus sesiones están más
                organizadas, te devolvemos el 100% de tu dinero.
              </p>
              <p className="text-lg font-bold text-emerald-700" data-id="element-167">
                Sin preguntas.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-12 px-4 bg-gray-50 border-t border-gray-200" data-id="element-168">
        <div className="max-w-3xl mx-auto text-center" data-id="element-169">
          <p className="text-gray-500 text-sm font-medium mb-1" data-id="element-170">
            PRODUCTO DIGITAL
          </p>
          <p className="text-gray-500 text-sm mb-1" data-id="element-171">
            Este producto es digital y se envía automáticamente después de la
            compra.
          </p>
          <p className="text-gray-500 text-sm mb-6" data-id="element-172">Acceso inmediato.</p>
          <p className="text-gray-400 text-xs" data-id="element-173">
            © 2024 MegaPack Psicopedagógico. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}